/** Sample data for local development (used by MSW + screens before the API is wired). */

import type { InboxItem } from "../features/inbox/inbox.ts";
import type { TonightMode } from "../features/tonight/mode.ts";

export interface MockMenuItem {
  id: string;
  name: string;
  nameTh: string;
  description: string;
  category: "Recommended" | "Beer" | "Cocktail" | "Bottle" | "Mixer" | "Snack" | "Food";
  /** Unit price in satang (integer minor units). */
  priceMinor: number;
  /** Icon name from the @jod/ui icon set, e.g. "IconBeer". */
  iconName: string;
  tags: string[];
  giftable: boolean;
  recommended?: boolean;
  /** Availability state (FR-ORDER-002). Undefined (mock data) treated as available. */
  isAvailable?: boolean;
  /** Contains alcohol (FR-CUS-064) — menu UI must badge these clearly. */
  alcohol?: boolean;
}

export interface MockTable {
  id: string;
  code: string;
  zone: string;
  people: number;
  /** English brand tag for the table's mode. */
  vibe: string;
  statusTh: string;
  mode: TonightMode;
  emoji: string;
  members: string[];
  bioTh: string;
  playing: boolean;
}

export interface MockJoinTable {
  code: string;
  zone: string;
  token: string;
  friendsCheckedIn: number;
  branchName: string;
  status: "available" | "order_only" | "closed";
  helperTh: string;
}

export const mockVenue = {
  id: "venue-001",
  name: "The Velvet Room",
  nameTh: "เดอะ เวลเวท รูม",
  session: "Friday Slow Night",
  zone: "ทองหล่อ · Bangkok",
  vibeTh: "ชิลๆ มีไลฟ์เบาๆ",
  liveAct: "Slowband กำลังเล่น",
  crowdTh: "กำลังคึก · 18 โต๊ะ",
  lastCall: "Last call 01:30",
};

export const mockJoinTables: MockJoinTable[] = [
  {
    code: "B4",
    zone: "Outdoor · ระเบียง",
    token: "qr-b4-tonight",
    friendsCheckedIn: 2,
    branchName: "Thonglor",
    status: "available",
    helperTh: "โต๊ะนี้เปิดทั้งสั่งอาหารและโซเชียลแบบยินยอม",
  },
  {
    code: "C2",
    zone: "Bar · หน้าเคาน์เตอร์",
    token: "qr-c2-tonight",
    friendsCheckedIn: 1,
    branchName: "Thonglor",
    status: "available",
    helperTh: "โต๊ะนี้อยู่ในโซน Bar และมีเพื่อนเช็คอินแล้ว",
  },
  {
    code: "NO-TABLE",
    zone: "Walk-in · ยังไม่ผูกโต๊ะ",
    token: "qr-branch-thonglor",
    friendsCheckedIn: 0,
    branchName: "Thonglor",
    status: "order_only",
    helperTh: "สแกน QR สาขาแล้ว แต่ยังไม่มีโต๊ะ — เริ่มแบบสั่งอย่างเดียวได้",
  },
];

export function findJoinTableByToken(token?: string): MockJoinTable | undefined {
  if (!token) return undefined;
  return mockJoinTables.find(
    (table) => table.token === token || table.code === token.toUpperCase(),
  );
}

export const MENU_CATEGORIES = [
  "Recommended",
  "Beer",
  "Cocktail",
  "Bottle",
  "Mixer",
  "Snack",
  "Food",
] as const;

export const mockMenu: MockMenuItem[] = [
  {
    id: "m1",
    name: "Singha Draft",
    nameTh: "สิงห์สด",
    description: "เบียร์สดแก้วเย็น เสิร์ฟพร้อมโฟมนุ่ม",
    category: "Beer",
    priceMinor: 12000,
    iconName: "IconBeer",
    tags: ["เย็นจัด"],
    giftable: true,
    recommended: true,
  },
  {
    id: "m2",
    name: "Asahi Pint",
    nameTh: "อาซาฮี ไพนต์",
    description: "ไพนต์เย็นๆ ดื่มง่ายทั้งคืน",
    category: "Beer",
    priceMinor: 16000,
    iconName: "IconBeer",
    tags: ["นำเข้า"],
    giftable: true,
  },
  {
    id: "m3",
    name: "Velvet Negroni",
    nameTh: "เวลเวท เนโกรนี",
    description: "จินบ้านผสมเวอร์มุทคั่วส้ม กลิ่นหอมลึก",
    category: "Cocktail",
    priceMinor: 28000,
    iconName: "IconCocktail",
    tags: ["signature", "ขม-หวาน"],
    giftable: true,
    recommended: true,
  },
  {
    id: "m4",
    name: "Lychee Slow",
    nameTh: "ลิ้นจี่ สโลว์",
    description: "ลิ้นจี่สดกับโซดา หอมเบาๆ",
    category: "Cocktail",
    priceMinor: 26000,
    iconName: "IconCocktail",
    tags: ["หวานน้อย"],
    giftable: true,
    recommended: true,
  },
  {
    id: "m5",
    name: "Tequila Gold",
    nameTh: "เตกีล่า โกลด์",
    description: "ช็อตเดียวพอเริ่มคืน",
    category: "Snack",
    priceMinor: 14000,
    iconName: "IconShot",
    tags: ["แรง"],
    giftable: true,
  },
  {
    id: "m6",
    name: "House Whisky",
    nameTh: "วิสกี้ประจำร้าน",
    description: "ขวดประจำร้าน เสิร์ฟพร้อมมิกเซอร์",
    category: "Bottle",
    priceMinor: 150000,
    iconName: "IconBottle",
    tags: ["แบ่งโต๊ะ"],
    giftable: false,
  },
  {
    id: "m7",
    name: "Soda / Mixer Set",
    nameTh: "เซ็ตมิกเซอร์",
    description: "โซดา น้ำแข็ง มะนาว ครบเซ็ต",
    category: "Mixer",
    priceMinor: 22000,
    iconName: "IconMixer",
    tags: ["รีฟิล"],
    giftable: true,
  },
  {
    id: "m8",
    name: "Truffle Fries",
    nameTh: "เฟรนช์ฟรายทรัฟเฟิล",
    description: "เฟรนช์ฟรายกรอบ กลิ่นทรัฟเฟิล",
    category: "Snack",
    priceMinor: 18000,
    iconName: "IconSnack",
    tags: ["ฮิต"],
    giftable: true,
    recommended: true,
  },
  {
    id: "m9",
    name: "Spicy Edamame",
    nameTh: "ถั่วแระเผ็ด",
    description: "ถั่วแระคลุกพริกเกลือ",
    category: "Snack",
    priceMinor: 11000,
    iconName: "IconSnack",
    tags: ["เผ็ดนิด"],
    giftable: true,
  },
  {
    id: "m10",
    name: "Crispy Pork & Rice",
    nameTh: "ข้าวหมูกรอบ",
    description: "หมูกรอบกับข้าวสวยร้อนๆ",
    category: "Food",
    priceMinor: 19000,
    iconName: "IconSnack",
    tags: ["อิ่ม"],
    giftable: false,
  },
  {
    id: "m11",
    name: "Virgin Sunset",
    nameTh: "เวอร์จิ้น ซันเซ็ต",
    description: "ม็อกเทลส้ม-เสาวรส ไม่มีแอลกอฮอล์",
    category: "Cocktail",
    priceMinor: 15000,
    iconName: "IconCoffee",
    tags: ["non-alc"],
    giftable: true,
  },
];

/** Curated giftable subset for the "Send Just One Drink" picker. */
export const giftItems = mockMenu.filter((m) => m.giftable).slice(0, 6);

export const giftMessages = ["ขอชนแก้วครับ/ค่ะ", "เพลงนี้ดี ส่งให้เลย", "เห็นโต๊ะคุณสนุกดี", "จากโต๊ะเราแบบเบาๆ"];

export const mockTables: MockTable[] = [
  {
    id: "C2",
    code: "C2",
    zone: "Bar",
    people: 2,
    vibe: "Let's Cheers",
    statusTh: "เปิดรับชนแก้ว",
    mode: "lets_cheers",
    emoji: "🍻",
    members: ["บีม", "พลอย"],
    bioTh: "มาฟังเพลงเฉยๆ ใครอยากชนแก้วทักได้",
    playing: true,
  },
  {
    id: "A1",
    code: "A1",
    zone: "Window",
    people: 3,
    vibe: "With Friends",
    statusTh: "มากันเป็นกลุ่ม",
    mode: "with_friends",
    emoji: "✨",
    members: ["มิ้น", "เจ", "ต้น"],
    bioTh: "ทีมเพื่อนเก่า เปิดโต๊ะแบบกลุ่ม",
    playing: false,
  },
  {
    id: "B7",
    code: "B7",
    zone: "Outdoor",
    people: 1,
    vibe: "Soft Open",
    statusTh: "เปิดตัวนิดนึง",
    mode: "soft_open",
    emoji: "🌙",
    members: ["นัท"],
    bioTh: "นั่งจิบเงียบๆ ทักมาก่อนได้นะ",
    playing: false,
  },
  {
    id: "D3",
    code: "D3",
    zone: "Stage",
    people: 4,
    vibe: "Let's Cheers",
    statusTh: "กำลังเล่นเกม",
    mode: "lets_cheers",
    emoji: "🎉",
    members: ["ฟ้า", "กานต์", "+2"],
    bioTh: "โต๊ะหน้าเวที เล่นโหวตเพลงอยู่ มาเล่นด้วยกัน",
    playing: true,
  },
];

/** Anonymized, consent-respecting live-activity lines for the home ticker. */
export const activityLines = [
  "เพิ่งมีโต๊ะส่งดื่มให้กันโซน Bar 🍸",
  "D3 กำลังโหวตเพลงอยู่ตอนนี้",
  "มีโต๊ะใหม่เปิดตัวที่ระเบียง",
  "Slowband ขึ้นเล่นเซ็ตสองแล้ว 🎶",
];

export const nicknameGlyphs = ["🌙", "🍸", "🎧", "🌶️", "⭐", "🦊"];

/** Seed inbox: one gift + one contact request, both awaiting consent. */
export const mockInbox: InboxItem[] = [
  {
    id: "i1",
    kind: "gift",
    fromTable: "C2",
    subject: "Singha Draft",
    subjectTh: "สิงห์สด",
    iconName: "IconBeer",
    message: "ขอชนแก้วครับ/ค่ะ",
    state: "pending",
  },
  {
    id: "i2",
    kind: "contact",
    fromTable: "A1",
    subject: "IG",
    message: "เห็นโต๊ะคุณสนุกดี ขอ IG ได้ไหม",
    state: "pending",
  },
];
